import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { AggregationType, getFormattedTick } from '../../helpers/chartUtils';
import { api } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import moment from 'moment';
import {
  MAILING_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_CHIP_COLOR,
  NEWSLETTER_ZONES,
  ZONE_LABELS,
  hasRevenueAttribution,
  type ZoneRevenue,
} from '../../helpers/mailingCategories';
import { buildDownloadUrl } from '../../helpers/apiUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Tooltip, Button } from '@mui/material';

interface RevenueBreakdown {
  webinar_revenue: number;
  webinar_registrations: number;
  plx_revenue: number;
  vnr_inline_revenue: number;
  vnr_slot_revenue: number;
  avg_cpa_applied: number;
  zones?: ZoneRevenue;
}

interface EtkpDetailRow {
  campaign_id: string;
  campaign_name: string;
  campaign_type: string;
  customer: string;
  send_date: string;
  emails_sent: number;
  revenue: number | null;
  etkp: number | null;
  revenue_attributed?: boolean;
  revenue_breakdown?: RevenueBreakdown;
}

interface EtkpDetailModalProps {
  open: boolean;
  onClose: () => void;
  period: string | null;
  aggregation: AggregationType;
  startDate: Date | null;
  endDate: Date | null;
  selectedCustomers: string[];
  selectedMailingTypes: string[];
  selectedZones?: string[];
  normalizeInactive?: boolean;
}

const ETKP_MIN_DATE = new Date('2025-08-01');

const EtkpDetailModal: React.FC<EtkpDetailModalProps> = ({
  open,
  onClose,
  period,
  aggregation,
  startDate,
  endDate,
  selectedCustomers,
  selectedMailingTypes,
  selectedZones,
  normalizeInactive = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<EtkpDetailRow[]>([]);

  useEffect(() => {
    if (!open || !period) return;

    let cancelled = false;
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const effectiveStart = startDate && startDate < ETKP_MIN_DATE ? ETKP_MIN_DATE : startDate;

        const params = new URLSearchParams({
          period,
          aggregation,
          start_date: moment(effectiveStart).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
        });
        selectedCustomers.forEach((c) => params.append('customer_id', c));
        if (selectedMailingTypes.length < MAILING_CATEGORIES.length) {
          params.append('mailing_types', selectedMailingTypes.join(','));
        }
        if (
          selectedZones &&
          selectedMailingTypes.includes('newsletter') &&
          selectedZones.length < NEWSLETTER_ZONES.length
        ) {
          params.append('nl_zones', selectedZones.join(','));
        }
        if (normalizeInactive) params.append('normalize', '1');

        const response = await api.get(`/api/etkp-development/details?${params}`);
        if (!cancelled) {
          setRows(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching ETKP details:', error);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    period,
    aggregation,
    startDate,
    endDate,
    selectedCustomers,
    selectedMailingTypes,
    selectedZones,
    normalizeInactive,
  ]);

  const formattedPeriod = period ? getFormattedTick(aggregation, period) : '';

  const totalEmailsSent = rows.reduce((sum, r) => sum + (Number(r.emails_sent) || 0), 0);
  // Totals use only rows with actual revenue attribution — keeps ETKP honest
  // when the filter includes newsletter/followup/other categories (they have
  // no attributable revenue but do contribute recipients).
  const attributedRows = rows.filter(
    (r) => (r.revenue_attributed ?? hasRevenueAttribution(r.campaign_type)) && r.revenue != null,
  );
  const totalRevenue = attributedRows.reduce((sum, r) => sum + (Number(r.revenue) || 0), 0);
  const attributedRecipients = attributedRows.reduce(
    (sum, r) => sum + (Number(r.emails_sent) || 0),
    0,
  );
  const totalEtkp =
    attributedRecipients > 0
      ? Number(((totalRevenue / attributedRecipients) * 1000).toFixed(2))
      : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Kampagnen-Details: {formattedPeriod}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" align="center" py={4}>
            Keine Kampagnen in diesem Zeitraum
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Kampagne
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Typ
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Kunde
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    Versanddatum
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Empfänger
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    Umsatz
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                    align="right"
                  >
                    ETKP
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const label = CATEGORY_LABELS[row.campaign_type] || row.campaign_type;
                  const color = CATEGORY_CHIP_COLOR[row.campaign_type] || 'default';
                  const attributed =
                    row.revenue_attributed ?? hasRevenueAttribution(row.campaign_type);
                  const isNewsletter = row.campaign_type === 'newsletter';
                  const zones = row.revenue_breakdown?.zones;
                  const sendDateStr = row.send_date
                    ? moment(row.send_date).format('YYYY-MM-DD')
                    : null;
                  return (
                    <React.Fragment key={`${row.campaign_id}-${index}`}>
                      <TableRow hover>
                        <TableCell
                          sx={{
                            color: 'black',
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{row.campaign_name || '—'}</span>
                            {isNewsletter && sendDateStr ? (
                              <Tooltip title="Newsletter-Vorschau in neuem Tab — gespeicherter Send aus ft_newsletter_mailings">
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<VisibilityIcon fontSize="small" />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const url = buildDownloadUrl(
                                      '/api/newsletter-ad-insertions/preview',
                                      {
                                        newsletter_date: sendDateStr,
                                        newsletter_type: 'finanztrends',
                                      },
                                    );
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                  }}
                                  sx={{ minWidth: 0, py: 0, px: 0.5, fontSize: 11 }}
                                >
                                  Vorschau
                                </Button>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={label} color={color} size="small" />
                        </TableCell>
                        <TableCell sx={{ color: 'black' }}>{row.customer || '—'}</TableCell>
                        <TableCell sx={{ color: 'black' }}>
                          {row.send_date ? moment(row.send_date).format('DD.MM.YYYY') : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {formatNumber(Number(row.emails_sent) || 0, 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {attributed && row.revenue != null
                            ? `${formatNumber(Number(row.revenue), 2)} \u20AC`
                            : '–'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'black' }}>
                          {attributed && row.etkp != null
                            ? `${formatNumber(Number(row.etkp), 2)} \u20AC`
                            : '–'}
                        </TableCell>
                      </TableRow>
                      {isNewsletter && zones ? (
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell colSpan={7} sx={{ py: 0.5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                fontSize: 11,
                                color: '#666',
                                pl: 2,
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>Zone-Split:</span>
                              {NEWSLETTER_ZONES.map((z) => {
                                const v = zones[z] || 0;
                                if (v === 0) return null;
                                return (
                                  <span key={z}>
                                    <strong>{ZONE_LABELS[z]}:</strong> {formatNumber(v, 2)} &euro;
                                  </span>
                                );
                              })}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {/* Summary row */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>Insgesamt</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>
                    {formatNumber(totalEmailsSent, 0)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>
                    {formatNumber(totalRevenue, 2)} &euro;
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'black' }}>
                    {formatNumber(totalEtkp, 2)} &euro;
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EtkpDetailModal;
